import { Link } from "react-router-dom";
import { Button } from "./ui/Button";
import { Panel } from "./Surface";
import { Icon } from "../app/Icon";

export interface OnboardingStep {
  key: string;
  label: string;
  hint?: string;
  done: boolean;
  href: string;
  cta: string;
}

/** First-run checklist: drives a new member to a fully set-up, verified, active
 *  account. Progress is derived from live data — no separate tracking. */
export function OnboardingChecklist({ steps, onDismiss }: { steps: OnboardingStep[]; onDismiss: () => void }) {
  const done = steps.filter((s) => s.done).length;
  const pct = Math.round((done / steps.length) * 100);
  // The first not-yet-done step is the one to nudge.
  const nextKey = steps.find((s) => !s.done)?.key;

  return (
    <Panel title="Get set up">
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono text-xs text-muted">{done} of {steps.length} complete</span>
        <button type="button" onClick={onDismiss} className="font-mono text-xs font-bold text-muted hover:text-ink">Dismiss</button>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-surface-2" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
        <div className="h-full rounded-full bg-accent transition-all" style={{ width: `${pct}%` }} />
      </div>
      <ul className="grid">
        {steps.map((s) => (
          <li key={s.key} className="flex items-center gap-3 border-t border-line py-3 first:border-t-0">
            <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-black ${s.done ? "bg-verified-bg text-verified" : "border border-line text-muted"}`}>
              {s.done ? <Icon name="verify" className="h-4 w-4" /> : steps.indexOf(s) + 1}
            </span>
            <div className="min-w-0 flex-1">
              <span className={`block text-sm font-bold ${s.done ? "text-muted line-through" : ""}`}>{s.label}</span>
              {s.hint && !s.done && <span className="block font-mono text-[11px] text-muted">{s.hint}</span>}
            </div>
            {!s.done && (
              <Link to={s.href}>
                <Button variant={s.key === nextKey ? "amber" : "ghost"}>{s.cta}</Button>
              </Link>
            )}
          </li>
        ))}
      </ul>
    </Panel>
  );
}
